import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { Component, createRef } from 'react';
import styled from 'styled-components';
import isWithinRange from 'date-fns/is_within_range';
import formatDate from 'date-fns/format';
import './DatePicker.css';
import getPrevMonth from './utils/getPrevMonth';
import getNextMonth from './utils/getNextMonth';
import clampMonthAndYear from './utils/clampMonthAndYear';
import { getYearsBetweenDates } from './getYearsBetweenDates';
import { CalendarMonth } from './CalendarMonth';
import { Dates } from './Dates';
import { ButtonLegacy } from '../Button/ButtonLegacy';
import { Input } from '../Form/Input';
import { tombac } from '../../shared/tombac';
import DateIcon from './icons/DateIcon';
import CancelIcon from './icons/CancelIcon';
import ChevronLeftIcon from './icons/ChevronLeftIcon';
import ChevronRightIcon from './icons/ChevronRightIcon';
import { propStyling } from '../../shared/propStyling';
import { extractStylingProps } from '../../shared/propStyling/propStylingUtils';
const INITIAL_STATE = {
  currentMonth: new Date().getMonth() + 1,
  currentYear: new Date().getFullYear()
};
function defaultParseValue(inputValue, dateFormat) {
  return inputValue && inputValue instanceof Date ? formatDate(inputValue, dateFormat) : '';
}
const CALENDAR_MONTH_CLASSNAMES = {
  availableClass: 'CalendarMonth-day--available DatePicker__day--available',
  betweenClass: 'CalendarMonth-day--between DatePicker__day--between',
  betweenEdgeClass: 'CalendarMonth-day--edge DatePicker__day--edge',
  startClass: 'CalendarMonth-day--start DatePicker__day--start',
  endClass: 'CalendarMonth-day--end DatePicker__day--end',
  todayClass: 'CalendarMonth-day--today DatePicker__day--today'
};
const Container = styled.div.withConfig({
  displayName: "DatePicker__Container"
})(["display:inline-block;position:relative;width:", ";", " & > .TombacInput{width:100%;}"], tombac.space(20), propStyling);
export class DatePicker extends Component {
  constructor(props) {
    var _props$isOpen;
    super(props);
    _defineProperty(this, "containerRef", /*#__PURE__*/createRef());
    this.state = {
      isFocused: false,
      isOpen: (_props$isOpen = props.isOpen) !== null && _props$isOpen !== void 0 ? _props$isOpen : false,
      value: props.parseValue(props.value, props.dateFormat),
      ...INITIAL_STATE
    };
    this.showNextMonth = this.showNextMonth.bind(this);
    this.showPrevMonth = this.showPrevMonth.bind(this);
    this.onYearSelected = this.onYearSelected.bind(this);
    this.returnToToday = this.returnToToday.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDayClick = this.handleDayClick.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
  }
  componentWillReceiveProps(props) {
    const {
      value,
      minDate,
      maxDate,
      dateFormat,
      parseValue,
      isOpen
    } = props;
    const month = value instanceof Date ? value.getUTCMonth() + 1 : this.state.currentMonth;
    const year = value instanceof Date ? value.getUTCFullYear() : this.state.currentYear;
    const {
      month: currentMonth,
      year: currentYear
    } = clampMonthAndYear(month, year, minDate, maxDate);
    this.setState(prevState => ({
      value: parseValue(value, dateFormat),
      currentMonth,
      currentYear,
      isOpen: isOpen !== null && isOpen !== void 0 ? isOpen : prevState.isOpen
    }));
  }
  componentDidMount() {
    document.addEventListener('click', this.handleDocumentClick);
  }
  componentWillUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen && this.props.isOpen !== undefined) {
      this.setState(() => {
        var _this$props$isOpen;
        return {
          isOpen: (_this$props$isOpen = this.props.isOpen) !== null && _this$props$isOpen !== void 0 ? _this$props$isOpen : false
        };
      });
    }
  }
  returnToToday() {
    this.setState({
      ...INITIAL_STATE
    });
  }
  showNextMonth() {
    this.setState(state => {
      const nextMonth = getNextMonth([state.currentMonth, state.currentYear]);
      const [currentMonth, currentYear] = nextMonth;
      return {
        ...state,
        currentMonth,
        currentYear
      };
    });
  }
  showPrevMonth() {
    this.setState(state => {
      const prevMonth = getPrevMonth([state.currentMonth, state.currentYear]);
      const [currentMonth, currentYear] = prevMonth;
      return {
        ...state,
        currentMonth,
        currentYear
      };
    });
  }
  onYearSelected(e) {
    const {
      minDate,
      maxDate
    } = this.props;
    const selectedMonth = this.state.currentMonth;
    const selectedYear = parseInt(e.target.value, 10);
    const {
      month: clampedMonth
    } = clampMonthAndYear(selectedMonth, selectedYear, minDate, maxDate);
    this.setState({
      currentYear: selectedYear,
      currentMonth: clampedMonth
    });
  }
  handleChange(e) {
    const {
      value
    } = e.target;
    this.setState({
      value
    });
  }
  handleBlur(e) {
    const {
      onChange,
      minDate,
      maxDate,
      onBlur
    } = this.props;
    if (onBlur) {
      onBlur(e);
      return;
    }
    const {
      value
    } = e.currentTarget;
    const [day, month, year] = value.split('/');
    const validDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    const isBetween = minDate && maxDate ? isWithinRange(validDate, minDate, maxDate) : false;
    if (!validDate) {
      if (onChange) onChange(null);
      return;
    }
    if (onChange && validDate && isBetween) {
      onChange(validDate);
    }
  }
  handleClick() {
    this.setState(() => {
      var _this$props$isOpen2;
      return {
        isOpen: (_this$props$isOpen2 = this.props.isOpen) !== null && _this$props$isOpen2 !== void 0 ? _this$props$isOpen2 : true,
        isFocused: true
      };
    });
  }
  handleDocumentClick(e) {
    var _this$containerRef$cu;
    if (!((_this$containerRef$cu = this.containerRef.current) !== null && _this$containerRef$cu !== void 0 && _this$containerRef$cu.contains(e.target))) this.setState(() => {
      var _this$props$isOpen3;
      return {
        isOpen: (_this$props$isOpen3 = this.props.isOpen) !== null && _this$props$isOpen3 !== void 0 ? _this$props$isOpen3 : false,
        isFocused: false
      };
    });
  }
  handleDayClick(e) {
    const {
      isOpen,
      onChange
    } = this.props;
    this.setState(() => ({
      isOpen: isOpen !== null && isOpen !== void 0 ? isOpen : false
    }));
    if (onChange) onChange(e);
  }
  handleKeyPress(e) {
    if (e.key === 'Enter') {
      this.handleBlur(e);
    }
  }
  render() {
    const {
      className,
      calendarClassName,
      clearable,
      disabled,
      startDate,
      endDate,
      minDate,
      maxDate,
      locale,
      weekStartsOn,
      placeholder,
      id,
      onChange,
      onFocus
    } = this.props;
    const {
      currentMonth,
      currentYear,
      isFocused,
      isOpen,
      value
    } = this.state;
    const currentPair = [currentYear, currentMonth];
    const months = [currentPair];
    const [stylingProps] = extractStylingProps(this.props);
    const renderedMonths = months.map(_ref => {
      let [year, month] = _ref;
      return /*#__PURE__*/React.createElement(CalendarMonth, _extends({
        key: `${year}-${month}`,
        year: year,
        month: month,
        onDayClicked: this.handleDayClick,
        startDate: startDate || endDate ? startDate : this.props.value,
        endDate: startDate || endDate ? endDate : this.props.value,
        minDate: minDate,
        maxDate: maxDate,
        locale: locale,
        weekStartsOn: weekStartsOn,
        hideMonthName: true
      }, CALENDAR_MONTH_CLASSNAMES));
    });
    const resetButton = /*#__PURE__*/React.createElement(CancelIcon, {
      className: "DatePicker-reset",
      onClick: () => onChange && onChange(undefined)
    });
    const input = /*#__PURE__*/React.createElement(Input, {
      className: "DatePicker-input",
      disabled: disabled,
      placeholder: placeholder,
      id: id,
      value: value,
      prepend: /*#__PURE__*/React.createElement(DateIcon, null),
      append: value !== '' && clearable && resetButton,
      highlighted: isFocused,
      onBlur: this.handleBlur,
      onChange: this.handleChange,
      onClick: this.handleClick,
      onFocus: onFocus,
      onKeyPress: this.handleKeyPress,
      autoComplete: "off"
    });
    const openCalendarClass = isOpen ? 'DatePicker-calendar--open' : '';
    const someDayOfTheMonth = new Date(Date.UTC(2000, currentMonth - 1, 2));
    const monthName = new Intl.DateTimeFormat(locale, {
      month: 'long'
    }).format(someDayOfTheMonth);
    const availableYears = getYearsBetweenDates(minDate, maxDate);
    const yearSelect = /*#__PURE__*/React.createElement("select", {
      className: "DatePicker-year-select",
      value: currentYear,
      onChange: this.onYearSelected
    }, availableYears.map(year => /*#__PURE__*/React.createElement("option", {
      key: year
    }, year)));
    const isPrevMonthAvailable = currentYear > minDate.getFullYear() || currentYear === minDate.getFullYear() && currentMonth > minDate.getMonth() + 1;
    const prevMonth = /*#__PURE__*/React.createElement(ButtonLegacy, {
      className: 'DatePicker-year-prev' + (isPrevMonthAvailable ? ' DatePicker-year-prev--active' : ''),
      onClick: isPrevMonthAvailable ? this.showPrevMonth : undefined,
      prepend: /*#__PURE__*/React.createElement(ChevronLeftIcon, null),
      variant: "flat",
      size: "s",
      type: "button"
    });
    const isNextMonthAvailable = currentYear < maxDate.getFullYear() || currentYear === maxDate.getFullYear() && currentMonth < maxDate.getMonth() + 1;
    const nextMonth = /*#__PURE__*/React.createElement(ButtonLegacy, {
      className: 'DatePicker-year-next' + (isNextMonthAvailable ? ' DatePicker-year-next--active' : ''),
      onClick: isNextMonthAvailable ? this.showNextMonth : undefined,
      prepend: /*#__PURE__*/React.createElement(ChevronRightIcon, null),
      variant: "flat",
      size: "s",
      type: "button"
    });
    const calendar = /*#__PURE__*/React.createElement("div", {
      className: `DatePicker-calendar ${openCalendarClass} ${calendarClassName}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "DatePicker-year"
    }, prevMonth, /*#__PURE__*/React.createElement("span", {
      className: "DatePicker-year-label"
    }, monthName, " ", yearSelect, ' '), nextMonth), /*#__PURE__*/React.createElement("div", {
      className: "DatePicker-months"
    }, renderedMonths));
    return /*#__PURE__*/React.createElement(Container, _extends({
      className: `TombacDatePicker ${className}`
    }, stylingProps, {
      ref: this.containerRef
    }), input, calendar);
  }
}
_defineProperty(DatePicker, "defaultProps", {
  className: '',
  calendarClassName: '',
  minDate: Dates.JANUARY_2008,
  maxDate: Dates.TODAY,
  dateFormat: 'YYYY-MM-DD',
  locale: 'en-GB',
  weekStartsOn: 1,
  parseValue: defaultParseValue
});
//# sourceMappingURL=DatePicker.js.map